import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";
import Modal from "../../../../components/Modal";
import DropDown from "../../../../components/DropDown";
import InputField from "../../../../components/InputField";
import Icon from "../../../../components/Icon";
import { useEditDetails } from "./EditDetailsData";
import { usePermissions } from "../../../../utils/roleUtils";
import DocDeficiencyModal from "../DocDeficiencyModal";
import DocApproveModal from "../DocApproveModal";
import {
  PRIMARY_FIELDS,
  KYC_ENTITY_FIELDS,
  KYC_GST_FIELDS,
  KYC_MSME_FIELDS,
  KYC_CONTACT_FIELDS,
  KYC_BANK_FIELDS,
  DOCS_BY_ENTITY_TYPE,
  DOCS_GST_YES,
  DOCS_GST_NO,
  DOCS_MSME_YES,
  DOCS_BANK,
} from "../../AllDataFields";
import "./index.scss";

// ── Generic field renderer ────────────────────────────────────────────────────
const renderField = (
  field,
  entity,
  handleChange,
  handleBlur,
  errors,
  readOnly,
) => {
  const {
    name,
    type,
    label,
    options,
    conditionalOn,
    conditionalValue,
    ...rest
  } = field;

  // Conditional visibility
  if (conditionalOn && entity[conditionalOn] !== conditionalValue) return null;

  const value =
    type === "checkbox" ? entity[name] || false : entity[name] || "";
  const error = errors[name] || "";

  if (type === "select") {
    return (
      <div key={name} className="form-group">
        <label className="input-label" htmlFor={name}>
          {label}
          {rest.required && <span className="required-mark"> *</span>}
        </label>
        <DropDown
          name={name}
          value={value}
          onChange={readOnly ? undefined : handleChange}
          options={options || []}
          error={error}
          fullWidth
          disabled={readOnly}
        />
      </div>
    );
  }

  return (
    <InputField
      key={name}
      name={name}
      label={label}
      type={type || "text"}
      value={value}
      onChange={readOnly ? undefined : handleChange}
      onBlur={readOnly ? undefined : handleBlur}
      error={error}
      readOnly={readOnly && type !== "checkbox"}
      disabled={readOnly && type === "checkbox"}
      {...rest}
    />
  );
};

const renderFields = (
  fields,
  entity,
  handleChange,
  handleBlur,
  errors,
  readOnly,
) =>
  fields.map((f) => {
    const el = renderField(
      f,
      entity,
      handleChange,
      handleBlur,
      errors,
      readOnly,
    );
    return el ? React.cloneElement(el, { key: f.name }) : null;
  });

const DocSlot = ({
  doc,
  uploadedFiles = [],
  onView,
  canApprove,
  canDeficiency,
  onDocApprove,
  onDocDeficiency,
  docDeficiencies = [],
}) => {
  const hasFiles = uploadedFiles.length > 0;
  const [deficiencyTarget, setDeficiencyTarget] = React.useState(null);
  const [approveTarget, setApproveTarget] = React.useState(null);

  // find existing deficiency for a given file
  const getExisting = (file) =>
    docDeficiencies.find((d) => d.docName === (file.fileName || doc.label))
      ?.reason || "";

  return (
    <>
      <div
        className={`doc-slot ${hasFiles ? "doc-slot--done" : "doc-slot--pending"}`}
      >
        <div className="doc-slot__header">
          <span className="doc-slot__icon">{hasFiles ? "✓" : "○"}</span>
          <span className="doc-slot__name">{doc.label}</span>
          {doc.required && <span className="mandatory-badge">Required</span>}
          {!hasFiles && <span className="doc-status pending">Pending</span>}
        </div>

        {hasFiles &&
          uploadedFiles.map((file, i) => {
            const existing = getExisting(file);
            return (
              <div key={i} className="doc-slot__file">
                <span>📄 {file.fileName}</span>
                {file.uploadedOn && (
                  <span className="doc-slot__date">
                    {new Date(file.uploadedOn).toLocaleDateString("en-IN", {
                      day: "2-digit",
                      month: "short",
                      year: "numeric",
                    })}
                  </span>
                )}
                <div className="doc-slot__actions">
                  <button
                    type="button"
                    className="view-doc-btn"
                    onClick={() => onView(file.filePath)}
                    title={`View ${file.fileName}`}
                  >
                    <Icon iconName="view" width={16} height={16} />
                  </button>
                  {canDeficiency && (
                    <button
                      type="button"
                      className={`doc-action-btn doc-action-btn--deficiency${existing ? " doc-action-btn--active" : ""}`}
                      onClick={() => setDeficiencyTarget(file)}
                      title={
                        existing
                          ? `Edit deficiency: ${existing}`
                          : "Mark deficiency"
                      }
                    >
                      ✗{existing && <span className="doc-action-btn__dot" />}
                    </button>
                  )}
                  {canApprove && (
                    <button
                      type="button"
                      className="doc-action-btn doc-action-btn--approve"
                      onClick={() => setApproveTarget(file)}
                      title="Approve document"
                    >
                      ✓
                    </button>
                  )}
                </div>
                {existing && (
                  <div className="doc-slot__deficiency-preview">
                    <span className="doc-slot__deficiency-label">
                      Deficiency:
                    </span>
                    <span className="doc-slot__deficiency-text">
                      {existing}
                    </span>
                  </div>
                )}
              </div>
            );
          })}
      </div>

      <DocDeficiencyModal
        isOpen={!!deficiencyTarget}
        docName={deficiencyTarget?.fileName || ""}
        existingReason={deficiencyTarget ? getExisting(deficiencyTarget) : ""}
        onClose={() => setDeficiencyTarget(null)}
        onSubmit={(reason) => {
          onDocDeficiency && onDocDeficiency(doc, deficiencyTarget, reason);
          setDeficiencyTarget(null);
        }}
      />
      <DocApproveModal
        isOpen={!!approveTarget}
        docName={approveTarget?.fileName || ""}
        onClose={() => setApproveTarget(null)}
        onConfirm={() => {
          onDocApprove && onDocApprove(doc, approveTarget);
          setApproveTarget(null);
        }}
      />
    </>
  );
};

const DocSection = ({
  title,
  docs,
  documents,
  onView,
  canApprove,
  canDeficiency,
  onDocApprove,
  onDocDeficiency,
  docDeficiencies,
}) => {
  if (!docs || docs.length === 0) return null;
  return (
    <div className="doc-inline-section">
      {title && <p className="doc-inline-section__title">{title}</p>}
      <div className="doc-inline-grid">
        {docs.map((doc) => {
          const matched = documents.find((d) =>
            d.documentName
              ?.toLowerCase()
              .includes(doc.label.toLowerCase().split(" ")[0]),
          );
          const uploadedFiles =
            matched?.files?.filter((f) => f.isUploaded) || [];
          return (
            <DocSlot
              key={doc.key}
              doc={doc}
              uploadedFiles={uploadedFiles}
              onView={onView}
              canApprove={canApprove}
              canDeficiency={canDeficiency}
              onDocApprove={onDocApprove}
              onDocDeficiency={onDocDeficiency}
              docDeficiencies={docDeficiencies}
            />
          );
        })}
      </div>
    </div>
  );
};

// ── Main Component ─────────────────────────────────────────────────────────────
const EditClientModal = ({
  isOpen,
  onClose,
  clientData,
  onSave,
  onApprove,
  onDeficiency,
}) => {
  const {
    formData: entity,
    errors,
    isSubmitting,
    departmentOptions,
    handleChange,
    handleBlur,
    handleUpdate,
    handleClose,
  } = useEditDetails(clientData, onSave, onClose);

  const { canApprove, canDeficiency, isReadOnly, canEdit, canUpdate } =
    usePermissions();

  const [isEditing, setIsEditing] = useState(!clientData?.viewOnly && canEdit);

  useEffect(() => {
    setIsEditing(!clientData?.viewOnly && canEdit);
  }, [clientData, canEdit]);

  const effectiveReadOnly = isReadOnly || !isEditing;

  // saved per-doc deficiencies: [{ docName, reason }]
  const [docDeficiencies, setDocDeficiencies] = useState([]);
  const [deficiencyReviewOpen, setDeficiencyReviewOpen] = useState(false);
  const [isActionLoading, setIsActionLoading] = useState(false);

  // reset when modal closes/opens
  useEffect(() => {
    if (!isOpen) setDocDeficiencies([]);
  }, [isOpen]);

  const handleViewDocument = (filePath) => {
    if (filePath) window.open(filePath, "_blank", "noopener,noreferrer");
  };

  // called from DocSlot — saves deficiency locally
  const handleDocDeficiency = (doc, file, reason) => {
    setDocDeficiencies((prev) => {
      // replace if same doc already has one
      const filtered = prev.filter(
        (d) => d.docName !== (file.fileName || doc.label),
      );
      return [...filtered, { docName: file.fileName || doc.label, reason }];
    });
    toast.info(`Deficiency saved for "${file.fileName || doc.label}"`);
  };

  const handleDocApprove = (doc, file) => {
    toast.success(`"${file.fileName || doc.label}" approved`);
  };

  // main Deficiency button → open review modal
  const handleDeficiencyButtonClick = () => {
    setDeficiencyReviewOpen(true);
  };

  // from review modal → call API
  const handleDeficiencySubmitToApi = async () => {
    if (!onDeficiency) return;
    if (docDeficiencies.length === 0) {
      toast.error("No deficiencies added. Please mark at least one document.");
      return;
    }
    setIsActionLoading(true);
    try {
      const email = (entity.emailAddress || "").trim().split(/\s+/)[0];
      const spoc = (entity.spocEmailAddress || "").trim().split(/\s+/)[0];
      // build combined description: "Doc Name: reason\nDoc Name: reason"
      const combined = docDeficiencies
        .map((d) => `${d.docName}: ${d.reason}`)
        .join("\n");
      await onDeficiency(entity.id, email, spoc, combined);
      setDeficiencyReviewOpen(false);
      setDocDeficiencies([]);
      handleClose();
    } finally {
      setIsActionLoading(false);
    }
  };

  const handleApproveClick = async () => {
    if (!onApprove) return;
    setIsActionLoading(true);
    try {
      const email = (entity.emailAddress || "").trim().split(/\s+/)[0];
      const spoc = (entity.spocEmailAddress || "").trim().split(/\s+/)[0];
      await onApprove(entity.id, email, spoc);
      handleClose();
    } finally {
      setIsActionLoading(false);
    }
  };

  if (!clientData) return null;

  const documents = clientData.documents || [];

  const resolvedPrimaryFields = PRIMARY_FIELDS.map((f) =>
    f.name === "aaDepartmentFunction"
      ? { ...f, options: departmentOptions }
      : f,
  );

  const entityTypeDocs = DOCS_BY_ENTITY_TYPE[entity.entityType] || [];
  const gstDocs =
    entity.isGSTRegistered === true || entity.isGSTRegistered === "yes"
      ? DOCS_GST_YES
      : entity.isGSTRegistered === false || entity.isGSTRegistered === "no"
        ? DOCS_GST_NO
        : [];
  const msmeDocs =
    entity.isRegisteredUnderMSME === true || entity.isMSMERegistered === "yes"
      ? DOCS_MSME_YES
      : [];

  const headerAction = canEdit ? (
    <Icon
      iconName={isEditing ? "view" : "edit"}
      className="modal-edit-toggle"
      onClick={() => setIsEditing((prev) => !prev)}
    />
  ) : (
    <button className="modal-close" onClick={handleClose}>
      &times;
    </button>
  );

  const docSectionProps = {
    documents,
    onView: handleViewDocument,
    canApprove,
    canDeficiency,
    onDocApprove: handleDocApprove,
    onDocDeficiency: handleDocDeficiency,
    docDeficiencies,
  };

  return (
    <>
      <Modal
        isOpen={isOpen}
        onClose={handleClose}
        title="Entity Details"
        size="large"
        headerAction={headerAction}
      >
        <form className="edit-entity-form">
          <div className="form-content">
            <div className="form-section">
              <h3 className="section-title">Primary Details</h3>
              <div className="form-grid">
                {renderFields(
                  resolvedPrimaryFields,
                  entity,
                  handleChange,
                  handleBlur,
                  errors,
                  effectiveReadOnly,
                )}
              </div>
            </div>

            <div className="form-section">
              <h3 className="section-title">Entity Information</h3>
              <div className="form-grid">
                {renderFields(
                  KYC_ENTITY_FIELDS,
                  entity,
                  handleChange,
                  handleBlur,
                  errors,
                  effectiveReadOnly,
                )}
              </div>
              <DocSection
                title={
                  entity.entityType
                    ? `Documents — ${entity.entityType.replace(/_/g, " ")}`
                    : null
                }
                docs={entityTypeDocs}
                {...docSectionProps}
              />
            </div>

            <div className="form-section">
              <h3 className="section-title">GST Information</h3>
              <div className="form-grid">
                {renderFields(
                  KYC_GST_FIELDS,
                  entity,
                  handleChange,
                  handleBlur,
                  errors,
                  effectiveReadOnly,
                )}
                {(entity.isGSTRegistered === true ||
                  entity.isGSTRegistered === "yes") && (
                  <>
                    <InputField
                      name="gstNumber"
                      label="GST Number"
                      maxLength={15}
                      value={entity.gstNumber || ""}
                      onChange={effectiveReadOnly ? undefined : handleChange}
                      onBlur={effectiveReadOnly ? undefined : handleBlur}
                      error={errors.gstNumber || ""}
                      readOnly={effectiveReadOnly}
                    />
                    <InputField
                      name="gstAddress"
                      label="GST Address"
                      value={entity.gstAddress || ""}
                      onChange={effectiveReadOnly ? undefined : handleChange}
                      readOnly={effectiveReadOnly}
                    />
                  </>
                )}
              </div>
              <DocSection
                title="GST / Address Proof"
                docs={gstDocs}
                {...docSectionProps}
              />
            </div>

            <div className="form-section">
              <h3 className="section-title">MSME Information</h3>
              <div className="form-grid">
                {renderFields(
                  KYC_MSME_FIELDS,
                  entity,
                  handleChange,
                  handleBlur,
                  errors,
                  effectiveReadOnly,
                )}
              </div>
              <DocSection
                title="MSME Documents"
                docs={msmeDocs}
                {...docSectionProps}
              />
            </div>

            <div className="form-section">
              <h3 className="section-title">Contact Person</h3>
              <div className="form-grid">
                {renderFields(
                  KYC_CONTACT_FIELDS,
                  entity,
                  handleChange,
                  handleBlur,
                  errors,
                  effectiveReadOnly,
                )}
              </div>
            </div>

            <div className="form-section">
              <h3 className="section-title">Bank Details</h3>
              <div className="form-grid">
                {renderFields(
                  KYC_BANK_FIELDS,
                  entity,
                  handleChange,
                  handleBlur,
                  errors,
                  effectiveReadOnly,
                )}
                {entity.thirdPartyPayment && (
                  <InputField
                    name="relationWithBeneficiary"
                    label="Relation with Beneficiary"
                    value={entity.relationWithBeneficiary || ""}
                    onChange={effectiveReadOnly ? undefined : handleChange}
                    fullWidth
                    readOnly={effectiveReadOnly}
                  />
                )}
              </div>
              {(entity.hasBankDetails === "yes" || entity.bankName) && (
                <DocSection
                  title="Bank Documents"
                  docs={DOCS_BANK}
                  {...docSectionProps}
                />
              )}
            </div>
          </div>

          <div className="form-actions">
            <button
              type="button"
              className="btn-cancel"
              onClick={handleClose}
              disabled={isSubmitting}
            >
              Cancel
            </button>
            {canUpdate && (
              <button
                type="button"
                className="btn-submit"
                disabled={!isEditing || isSubmitting}
                onClick={handleUpdate}
              >
                {isSubmitting ? "Updating..." : "Update"}
              </button>
            )}
            {canDeficiency && (
              <button
                type="button"
                className="btn-submit-reject"
                disabled={isSubmitting || isActionLoading}
                onClick={handleDeficiencyButtonClick}
              >
                {docDeficiencies.length > 0
                  ? `Deficiency (${docDeficiencies.length})`
                  : "Deficiency"}
              </button>
            )}
            {canApprove && (
              <button
                type="button"
                className="btn-submit-approve"
                disabled={isSubmitting || isActionLoading}
                onClick={handleApproveClick}
              >
                {isActionLoading ? "Approving..." : "Approve"}
              </button>
            )}
          </div>
        </form>
      </Modal>

      {/* Deficiency Review Modal — editable list, then submits to API */}
      <Modal
        isOpen={deficiencyReviewOpen}
        onClose={() => setDeficiencyReviewOpen(false)}
        title="Submit Deficiency"
        size="small"
      >
        <div className="doc-deficiency-review">
          {docDeficiencies.length === 0 ? (
            <div className="doc-deficiency-review__empty">
              <span className="doc-deficiency-review__empty-icon">⚠️</span>
              <p>No deficiencies added yet.</p>
              <p className="doc-deficiency-review__empty-hint">
                Close this and click ✗ on individual documents first.
              </p>
            </div>
          ) : (
            <>
              <p className="doc-deficiency-review__hint">
                Review and edit before submitting — {docDeficiencies.length}{" "}
                document{docDeficiencies.length > 1 ? "s" : ""}
              </p>
              <ul className="doc-deficiency-review__list">
                {docDeficiencies.map((d, i) => (
                  <li key={i} className="doc-deficiency-review__item">
                    <div className="doc-deficiency-review__item-header">
                      <span className="doc-deficiency-review__item-doc">
                        📄 {d.docName}
                      </span>
                      <button
                        type="button"
                        className="doc-deficiency-review__item-remove"
                        onClick={() =>
                          setDocDeficiencies((prev) =>
                            prev.filter((_, idx) => idx !== i),
                          )
                        }
                        title="Remove"
                      >
                        ×
                      </button>
                    </div>
                    <textarea
                      className="doc-deficiency-review__item-textarea"
                      rows={2}
                      value={d.reason}
                      onChange={(e) =>
                        setDocDeficiencies((prev) =>
                          prev.map((item, idx) =>
                            idx === i
                              ? { ...item, reason: e.target.value }
                              : item,
                          ),
                        )
                      }
                      placeholder="Edit reason..."
                    />
                  </li>
                ))}
              </ul>
            </>
          )}
          <div className="doc-deficiency-review__actions">
            <button
              type="button"
              className="btn-cancel"
              onClick={() => setDeficiencyReviewOpen(false)}
            >
              Cancel
            </button>
            <button
              type="button"
              className="btn-submit-reject"
              disabled={
                docDeficiencies.length === 0 ||
                docDeficiencies.some((d) => !d.reason.trim()) ||
                isActionLoading
              }
              onClick={handleDeficiencySubmitToApi}
            >
              {isActionLoading ? "Submitting..." : "Submit Deficiency"}
            </button>
          </div>
        </div>
      </Modal>
    </>
  );
};

export default EditClientModal;
